export interface IamTokenResponse {
  accessToken: string | null;
  idToken: string | null;
  refreshToken: string | null;
  tokenType: string;
  expiresIn: number | null;
}

export interface IamLogoutResponse {
  logoutUrl: string;
}

export interface AvailableApp {
  appId: string;
  appName: string;
  appUrl: string;
}

function getIamApiBase(): string {
  const configured = import.meta.env.VITE_IAM_API_BASE as string | undefined;
  if (configured) {
    return configured.replace(/\/$/, "");
  }
  return "/api/v2/sami-iam";
}

async function iamFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const url = `${getIamApiBase()}${path}`;
  const res = await fetch(url, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(body.trim() || `IAM request failed: ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export async function refreshAccessToken(
  refreshToken: string,
): Promise<IamTokenResponse> {
  return iamFetch<IamTokenResponse>("/auth/refresh", {
    method: "POST",
    body: JSON.stringify({ refreshToken }),
  });
}

export async function logoutWithRefreshToken(
  refreshToken: string | null,
  accessToken: string | null,
  idToken?: string | null,
): Promise<IamLogoutResponse> {
  const headers: Record<string, string> = {};
  const idTokenHint = idToken ?? accessToken;
  if (idTokenHint) {
    headers.Authorization = `Bearer ${idTokenHint}`;
  }
  return iamFetch<IamLogoutResponse>("/auth/logout", {
    method: "POST",
    headers,
    body: JSON.stringify({ refreshToken: refreshToken ?? undefined }),
  });
}

/**
 * Start SSO via the IAM BFF. Browser navigates to /auth/login; Cognito returns to
 * /auth/callback on the BFF, which exchanges the code and redirects to the SPA.
 */
export function redirectToIamLogin(): void {
  const params = new URLSearchParams();
  const appId = import.meta.env.VITE_IAM_APP_ID;
  const returnUrl = import.meta.env.VITE_IAM_RETURN_URL;
  if (appId?.trim()) {
    params.set("appId", appId.trim());
  } else if (returnUrl?.trim()) {
    params.set("returnUrl", returnUrl.trim());
  }
  
  const query = params.toString();
  window.location.assign(`${getIamApiBase()}/auth/login?${query}`);
}

/** Enabled apps for pre-login app selector UIs. */
export async function getAvailableApps(): Promise<AvailableApp[]> {
  return iamFetch<AvailableApp[]>("/auth/available-apps");
}

/** Parse tokens returned by the BFF after /auth/callback (in hash-route query). */
export function parseBffCallbackTokens(search: string): IamTokenResponse {
  const params = new URLSearchParams(
    search.startsWith("?") ? search : search ? `?${search}` : "",
  );
  const error = params.get("error");
  if (error) {
    throw new Error(error);
  }

  const idToken = params.get("idToken");
  if (!idToken) {
    throw new Error("Missing ID token from IAM callback");
  }

  const accessToken = params.get("accessToken");
  const refreshToken = params.get("refreshToken");
  const expiresInRaw = params.get("expiresIn");

  return {
    accessToken: accessToken || null,
    idToken,
    refreshToken: refreshToken || null,
    tokenType: "Bearer",
    expiresIn: expiresInRaw ? Number(expiresInRaw) : null,
  };
}
